import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing } from 'lucide-react';
import { Button, useToast } from '@/components/ui';
import { useStandalone } from '@/lib/app-mode';
import { dismissPushNudge, enablePush, pushNudgeDismissed, pushState } from '@/lib/push';

/**
 * «فعّل التنبيهات على هذا الجهاز» — أولَ ما يُفتح التطبيقُ مثبَّتاً والإذنُ لم يُسأل بعد والمنصةُ
 * مفعِّلةٌ للإشعارات. **الإذنُ لا يُطلب عند التحميل أبداً**: البطاقةُ تعرض زرّاً، والزرُّ لمسةُ
 * المستخدم التي يشترطها سفاري. و«ليس الآن» يُسكتها أسبوعين (`localStorage` ملفوفاً — محجوبُه
 * يعيدها في الفتح التالي لا أكثر).
 */
export function PushNudge() {
  const standalone = useStandalone();
  const qc = useQueryClient();
  const toast = useToast();
  const [hidden, setHidden] = useState(() => pushNudgeDismissed());
  const [busy, setBusy] = useState(false);
  const permission = typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
  // الحالةُ تجلب المفتاحَ العامّ **مسبقاً** فيكون الضغطُ نداءَ إذنٍ بلا شبكةٍ قبله
  // في المتصفّح أيضاً لا في التطبيق المثبَّت وحده (20 أكتوبر 2026 — «الإشعارات ما تجيني لما يكون التطبيق مقفل»): أندرويد يصله الإشعارُ
  // من المتصفّح والتطبيقُ مغلق. وآيفون يحتاج التثبيت — وهذا ما تقوله `pushState` ('ios-install') فلا تُعرض البطاقة
  const state = useQuery({ queryKey: ['me', 'push-state'], queryFn: pushState, enabled: !hidden && permission === 'default' });

  if (hidden || permission !== 'default' || state.data !== 'off') return null;

  async function enable() {
    setBusy(true);
    try {
      const s = await enablePush();
      qc.setQueryData(['me', 'push-state'], s);
      if (s === 'on') toast.push('success', 'فُعّلت الإشعارات على هذا الجهاز.');
      setHidden(true);
    } catch {
      toast.push('error', 'تعذّر تفعيل الإشعارات على هذا الجهاز.');
    } finally { setBusy(false); }
  }

  return (
    <aside className="push-nudge" aria-label="تفعيل التنبيهات">
      <span className="push-nudge__icon" aria-hidden><BellRing size={20} /></span>
      <div className="push-nudge__text">
        <b>فعّل التنبيهات على هذا الجهاز</b>
        <span className="caption">يصلك إشعارٌ على هذا الجهاز — جوالاً كان أو حاسوباً — حتى ورفرفة مغلقة: طلبٌ أو موافقةٌ أو رسالةٌ أو موعد، بلا أسماءٍ ولا تفاصيل.{standalone ? '' : ' والأفضل تثبيتُ رفرفة كتطبيق.'}</span>
      </div>
      <div className="push-nudge__actions">
        <Button size="sm" variant="primary" loading={busy} onClick={() => void enable()}>فعّل</Button>
        <Button size="sm" variant="ghost" onClick={() => { dismissPushNudge(); setHidden(true); }}>ليس الآن</Button>
      </div>
    </aside>
  );
}
